import { loadStripeTerminal } from "@stripe/terminal-js";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authHeader, clearToken, isUnauthorized } from "../utils/staffAuth";
import {
  STITCHED_NAME_MAX,
  basketLines,
  canCancelSale,
  lineLabel,
  rowTitle,
  sellableRows,
  basketSubtotal,
  money,
  needsAName,
  payBlocker,
  saleBody,
  stepQty,
  stockNote,
} from "../utils/products";

const pathURL = process.env.REACT_APP_API_URL;
// The same reader the garment checkout uses (TerminalCheckoutForm.jsx).
const TERMINAL_LOCATION_ID =
  process.env.REACT_APP_STRIPE_TERMINAL_LOCATION_ID || "tml_Gp0bwwHQWlsS5n";

const SIGNED_OUT =
  "This device has been signed out. Refresh the page, sign in again, and take the payment. Nothing was charged.";

/**
 * Sell retail products on the reader (Calley, 2026-10-12).
 *
 * A product sale is its own small sale with its own tap. It never touches
 * the garment basket or an order (fxrybackend helper/products.js says why),
 * so this screen keeps nothing in the cart store and leaves a garment order
 * in progress exactly as it was.
 *
 * It used to ask "is this with a pick-up?" before the Pay button would work,
 * which tagged the sale and fed one figure on the Products page. Calley, 31
 * Oct 2026: nobody cares. It also read like a question about which basket
 * the pin went in, when the answer never changed the sale, the price, the
 * tax or what the customer pays. Gone, with the order lookup that hung off
 * it. Add, and take the payment.
 *
 * Only product ids and quantities are sent. The server prices the sale from
 * the product list, works out the sales tax and tells the reader its own
 * total; the figures shown here come back from it.
 *
 * The sale is saved before the card is asked for, then settled from Stripe's
 * own answer after the tap, so a sale can't be charged and lost.
 */
function Products() {
  const navigate = useNavigate();
  const [products, setProducts] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [qtyById, setQtyById] = useState({});
  // The name Natalie chainstitches, when a made-to-order envelope is in the
  // basket. Nothing else on this screen is personalised.
  const [stitchedName, setStitchedName] = useState("");
  const [quote, setQuote] = useState(null); // the server's figures
  // idle | connecting | collecting | cancelling | processing | recording |
  // paid | error
  const [stage, setStage] = useState("idle");
  const [message, setMessage] = useState("");
  const [paid, setPaid] = useState(null);
  const terminalRef = useRef(null);
  // The stage as it is right now. `stage` inside a click handler is the value
  // that render captured, and Cancel has to know whether the card has reached
  // processPayment yet: the one difference between "nothing was charged" and
  // "that is a refund now".
  const stageRef = useRef("idle");
  // Set when staff press Cancel, so the sale still running below stops
  // quietly instead of reporting a decline nobody caused.
  const stoppedRef = useRef(false);
  // The sale to call off, once the server has saved it.
  const saleRef = useRef(null);

  /** Move to a stage, keeping the ref the Cancel button reads in step. */
  const enter = (next) => {
    stageRef.current = next;
    setStage(next);
  };

  const loadProducts = () =>
    axios
      .get(`${pathURL}products/for-sale`, { headers: authHeader() })
      .then((response) => setProducts(response.data.items || []))
      .catch((error) => {
        if (isUnauthorized(error)) {
          clearToken();
          setLoadError(SIGNED_OUT);
          return;
        }
        setLoadError("Couldn't load the products. Refresh the page.");
      });

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lines = useMemo(
    () => basketLines(products || [], qtyById),
    [products, qtyById],
  );
  const subtotal = basketSubtotal(lines);
  const body = useMemo(
    () => saleBody(lines, { stitchedName }),
    [lines, stitchedName],
  );
  const basketKey = JSON.stringify(body.lines);
  const busy = !["idle", "error"].includes(stage);
  const canCancel = canCancelSale(stage);
  const wantsAName = needsAName(lines);
  const blocker = payBlocker(lines, stitchedName);

  // The server's tax and total for the basket as it stands. Asked a moment
  // after the last change, and thrown away if the basket moved on meanwhile.
  useEffect(() => {
    setQuote(null);
    if (!lines.length) return undefined;
    let stale = false;
    const timer = setTimeout(() => {
      axios
        .post(
          `${pathURL}products/sales/terminal-intent`,
          { lines: body.lines, preview: true },
          { headers: authHeader() },
        )
        .then((response) => {
          if (!stale) setQuote(response.data);
        })
        .catch(() => {
          // The total is worked out again when Pay is pressed, and a failure
          // there is shown. Here the tax line just stays blank.
        });
    }, 350);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketKey]);

  const fail = (text) => {
    stageRef.current = "error";
    setStage("error");
    setMessage(text);
  };

  /** The reader, connected. Kept between sales so the second one is quick. */
  const connectReader = async (signedOut) => {
    let terminal = terminalRef.current;
    if (!terminal) {
      const StripeTerminal = await loadStripeTerminal();
      terminal = StripeTerminal.create({
        onFetchConnectionToken: async () => {
          try {
            const resp = await axios.post(
              `${pathURL}order/terminal/connection-token`,
              null,
              { headers: authHeader() },
            );
            return resp.data.secret;
          } catch (tokenError) {
            // Stripe's SDK swallows this 401 and reports its own failure,
            // so remember it and tell staff to sign in again.
            if (isUnauthorized(tokenError)) signedOut.yes = true;
            throw tokenError;
          }
        },
        onUnexpectedReaderDisconnect: () => {
          terminalRef.current = null;
        },
      });
      terminalRef.current = terminal;
    }
    if (terminal.getConnectionStatus() === "connected") return terminal;
    const discovered = await terminal.discoverReaders({
      location: TERMINAL_LOCATION_ID,
    });
    if (signedOut.yes) throw new Error("signed-out");
    if (discovered.error || !discovered.discoveredReaders?.length) {
      throw new Error(
        "Couldn't find the reader. Make sure it's powered on and connected to Wi-Fi. Nothing was charged.",
      );
    }
    const connected = await terminal.connectReader(
      discovered.discoveredReaders[0],
    );
    if (signedOut.yes) throw new Error("signed-out");
    if (connected.error) {
      throw new Error(
        `${connected.error.message || "Couldn't connect to the reader."} Nothing was charged.`,
      );
    }
    return terminal;
  };

  const cancelSale = (sale) =>
    axios
      .post(`${pathURL}products/sales/${sale.id}/cancel`, null, {
        headers: authHeader(),
      })
      .catch(() => {});

  /**
   * Staff press Cancel: the customer changed their mind at the reader.
   *
   * Stopping the reader comes first. The reader is driven from this screen,
   * so while it is still asking for a card Stripe will not let the payment
   * be cancelled underneath it, and before this existed there was no way out
   * of the tap at all: the reader kept asking and the only escape was to
   * restart it (Calley, 4 Nov 2026).
   */
  const cancelPayment = async () => {
    if (stoppedRef.current) return;
    if (!canCancelSale(stageRef.current)) {
      setMessage(
        "The card is being read now. Wait for it to finish, then refund it if they don't want it.",
      );
      return;
    }
    stoppedRef.current = true;
    enter("cancelling");
    setMessage("Stopping the payment. Nothing has been charged.");

    try {
      await terminalRef.current?.cancelCollectPaymentMethod();
    } catch (readerError) {
      // The reader had already finished asking, or was never asking. The
      // sale below is what decides whether money can still move.
      console.error("[PRODUCTS] stopping the reader:", readerError);
    }

    const sale = saleRef.current;
    if (sale) {
      const answer = await axios
        .post(`${pathURL}products/sales/${sale.id}/cancel`, null, {
          headers: authHeader(),
        })
        .catch(() => null);
      // The tap beat the Cancel by a moment: the money is taken, so say so
      // rather than leaving staff thinking it was called off.
      if (answer?.data?.result === "paid") {
        saleRef.current = null;
        return fail(
          `THE CARD WAS CHARGED ${money(sale.total_cents)} just as Cancel was pressed. Do not charge it again. The sale ${sale.ref} is on the Products page. Refund it there if they don't want it.`,
        );
      }
      if (!answer || answer.data?.result !== "cancelled") {
        saleRef.current = null;
        return fail(
          `Couldn't call off sale ${sale.ref} at Stripe. Nothing has been charged, but check ${sale.ref} in Stripe before taking another payment.`,
        );
      }
    }

    saleRef.current = null;
    // The basket and its total stay as they were, so staff can take the
    // payment again straight away. The figures are only re-asked when the
    // basket changes, and Take payment starts a new sale at Stripe anyway.
    enter("idle");
    setMessage("");
    return undefined;
  };

  const takePayment = async () => {
    if (blocker || busy) return;
    const signedOut = { yes: false };
    let sale = null;
    let charged = false;
    stoppedRef.current = false;
    saleRef.current = null;
    try {
      enter("connecting");
      setMessage("Connecting to the reader...");
      const terminal = await connectReader(signedOut);

      // The server prices the sale and saves it before any card is asked for.
      const started = await axios.post(
        `${pathURL}products/sales/terminal-intent`,
        body,
        { headers: authHeader() },
      );
      sale = started.data;
      saleRef.current = sale;
      setQuote(sale);

      enter("collecting");
      setMessage(
        `Tap, insert, or swipe the card on the reader now. ${money(sale.total_cents)}`,
      );
      const collected = await terminal.collectPaymentMethod(sale.clientSecret);
      // Staff pressed Cancel: stopping the reader is what ended the wait
      // above, and cancelPayment has already called the sale off and said so
      // on screen. Saying anything here would talk over it.
      if (stoppedRef.current) return undefined;
      if (collected.error) {
        await cancelSale(sale);
        return fail(
          `${collected.error.message || "The card was declined or the tap was cancelled."} Nothing was charged.`,
        );
      }

      enter("processing");
      setMessage("Processing payment...");
      const processed = await terminal.processPayment(collected.paymentIntent);
      if (processed.error || processed.paymentIntent?.status !== "succeeded") {
        // The server checks with Stripe before cancelling: if the money did
        // land after all, it settles the sale instead.
        const answer = await axios
          .post(`${pathURL}products/sales/${sale.id}/cancel`, null, {
            headers: authHeader(),
          })
          .catch(() => null);
        if (answer?.data?.result !== "paid") {
          return fail(
            `${processed.error?.message || "The payment did not go through."} Nothing was charged.`,
          );
        }
      }
      charged = true;

      enter("recording");
      setMessage("Payment approved. Saving the sale...");
      await axios.post(
        `${pathURL}products/sales/${sale.id}/terminal-settle`,
        null,
        { headers: authHeader() },
      );
      setPaid(sale);
      saleRef.current = null;
      enter("paid");
      setMessage("");
    } catch (error) {
      console.error("[PRODUCTS] sale failed:", error);
      // Staff pressed Cancel and the reader threw on the way out rather than
      // answering. cancelPayment owns the screen from there.
      if (stoppedRef.current && !charged) return undefined;
      if (charged) {
        // The card went through and only the record of it failed. The sale
        // is saved, and the Products page settles it on its next load.
        return fail(
          `THE CARD WAS CHARGED ${money(sale.total_cents)}. Do not charge it again. The sale ${sale.ref} will show on the Products page shortly. If it doesn't, send ${sale.ref} to hello@thefxry.com.`,
        );
      }
      if (sale) await cancelSale(sale);
      if (signedOut.yes || isUnauthorized(error)) {
        clearToken();
        return fail(SIGNED_OUT);
      }
      return fail(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong talking to the reader. Nothing was charged.",
      );
    }
    return undefined;
  };

  const newSale = () => {
    setQtyById({});
    setStitchedName("");
    setQuote(null);
    setPaid(null);
    stoppedRef.current = false;
    saleRef.current = null;
    enter("idle");
    setMessage("");
    loadProducts();
  };

  const total = quote?.total_cents;

  return (
    <section className="items-section same-bg-section relative">
      <div className="container padding-x-all">
        <div className="products-wrap">
          <div className="products-screen">
            <button
              type="button"
              className="products-back"
              onClick={() => navigate("/")}
              disabled={busy && stage !== "paid"}
            >
              ← Garments
            </button>
            <h2>Products</h2>

            {stage === "paid" && paid ? (
              <div className="wears-box products-paid" role="status">
                <h5>Paid</h5>
                <p className="products-paid-total">{money(paid.total_cents)}</p>
                <ul className="products-lines">
                  {paid.lines.map((line) => (
                    <li key={`${line.name}·${line.motif || ""}`}>
                      <span>
                        {line.qty} × {lineLabel(line)}
                      </span>
                      <span>{money(line.amount_cents)}</span>
                    </li>
                  ))}
                  <li>
                    <span>Sales tax</span>
                    <span>{money(paid.tax_cents)}</span>
                  </li>
                </ul>
                <p className="para">Sale {paid.ref}</p>
                <div className="products-paid-actions">
                  <button
                    type="button"
                    className="section-main-btn"
                    onClick={newSale}
                  >
                    New sale
                  </button>
                  <button
                    type="button"
                    className="custom-item-btn"
                    onClick={() => navigate("/")}
                  >
                    Back to garments
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="para">Add what they are buying.</p>

                <div className="wears-box">
                  {loadError && <p className="para">{loadError}</p>}
                  {!products && !loadError && (
                    <p className="para">Loading...</p>
                  )}
                  {products && products.length === 0 && (
                    <p className="para">
                      No products are on sale yet. Add them in superadmin, under
                      Manage, Products.
                    </p>
                  )}
                  <ul className="products-list">
                    {sellableRows(products || []).map(
                      ({ product, motif, key }) => {
                        const qty = qtyById[key] || 0;
                        const label = rowTitle(product, motif);
                        return (
                          <li
                            key={key}
                            className={
                              qty > 0 ? "products-row chosen" : "products-row"
                            }
                          >
                            <div className="products-name">
                              <p>{label}</p>
                              <p className="para">
                                {money(product.price_cents)} ·{" "}
                                {stockNote(product)}
                              </p>
                            </div>
                            <div className="products-stepper">
                              <button
                                type="button"
                                aria-label={`One fewer ${label}`}
                                disabled={busy || qty === 0}
                                onClick={() =>
                                  setQtyById((all) => ({
                                    ...all,
                                    [key]: stepQty(all[key], -1),
                                  }))
                                }
                              >
                                −
                              </button>
                              <span aria-live="polite">{qty}</span>
                              <button
                                type="button"
                                aria-label={`One more ${label}`}
                                disabled={busy}
                                onClick={() =>
                                  setQtyById((all) => ({
                                    ...all,
                                    [key]: stepQty(all[key], 1),
                                  }))
                                }
                              >
                                +
                              </button>
                            </div>
                          </li>
                        );
                      },
                    )}
                  </ul>
                </div>

                {wantsAName && (
                  <div className="field-box">
                    <label className="label-on-line">Name to stitch</label>
                    <input
                      value={stitchedName}
                      disabled={busy}
                      maxLength={STITCHED_NAME_MAX}
                      onChange={(e) => setStitchedName(e.target.value)}
                    />
                    <p className="para">
                      Natalie makes this one to order, so the customer comes
                      back for it. It goes on the Envelopes to do list in
                      superadmin.
                    </p>
                  </div>
                )}

                {lines.length > 0 && (
                  <div className="wears-box">
                    <ul className="products-lines">
                      {lines.map((line) => (
                        <li key={line.key}>
                          <span>
                            {line.qty} × {lineLabel(line)}
                          </span>
                          <span>{money(line.amount_cents)}</span>
                        </li>
                      ))}
                      <li>
                        <span>Sales tax</span>
                        <span>{quote ? money(quote.tax_cents) : "..."}</span>
                      </li>
                      <li className="products-total">
                        <span>Total</span>
                        <span>
                          {quote ? money(total) : `${money(subtotal)} + tax`}
                        </span>
                      </li>
                    </ul>
                  </div>
                )}

                {message && (
                  <div
                    className={
                      stage === "error"
                        ? "terminal-payment-status products-status error"
                        : "terminal-payment-status products-status"
                    }
                    role="status"
                  >
                    <p>{message}</p>
                  </div>
                )}

                {canCancel && (
                  <button
                    type="button"
                    className="text-black products-cancel"
                    onClick={cancelPayment}
                  >
                    Cancel payment
                  </button>
                )}

                <button
                  type="button"
                  className={
                    blocker || busy
                      ? "section-main-btn1 products-pay"
                      : "section-main-btn products-pay"
                  }
                  disabled={Boolean(blocker) || busy}
                  onClick={takePayment}
                >
                  {busy
                    ? "Taking payment..."
                    : stage === "error"
                      ? "Try again on the reader"
                      : `Take payment${quote ? ` ${money(total)}` : ""}`}
                </button>
                {blocker && lines.length > 0 && (
                  <p className="para products-blocker">{blocker}</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Products;
